import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, ConfirmDialog, Input } from '@mlm/ui';
import {
  addVariationOption,
  createVariationType,
  deleteVariationOption,
  deleteVariationType,
  listVariationTypes,
  updateVariationOption,
  updateVariationType,
  type VariationOption,
  type VariationType,
} from '../lib/api';

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

/**
 * Variation Types CMS. Admin-managed vocabulary (Size, Color, …) that products
 * use to define their variation axes. Each type owns a list of option values
 * (S/M/L; Red/Blue). A type in use by a product can't be deleted — deactivate it.
 */
export function VariationTypesPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['variation-types'], queryFn: () => listVariationTypes(true) });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<VariationType | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['variation-types'] });
  const create = useMutation({
    mutationFn: createVariationType,
    meta: { successMessage: 'Variation type created.' },
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteVariationType(id),
    meta: { successMessage: 'Variation type deleted.' },
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  const types = query.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Variation Types</h1>
        <Button onClick={() => setCreating(true)}>+ New type</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Define the axes a product can vary by (Size, Color, …) and each axis's values. A product
        picks which types it uses, then a price matrix is generated for every combination.
      </p>

      {creating && (
        <TypeForm
          submitting={create.isPending}
          error={create.error}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
        />
      )}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : types.length === 0 ? (
        <p className="text-sm text-muted-foreground">No variation types yet.</p>
      ) : (
        types.map((t) => (
          <TypeCard key={t.id} type={t} onChanged={invalidate} onDelete={() => setDeleting(t)} />
        ))
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete "${deleting?.name}"?`}
        description="Only allowed if no product uses this type. Otherwise deactivate it instead."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
      {errText(remove.error) && <p className="text-sm text-destructive">{errText(remove.error)}</p>}
    </div>
  );
}

function TypeCard({
  type,
  onChanged,
  onDelete,
}: {
  type: VariationType;
  onChanged: () => void;
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['variation-types'] });

  const update = useMutation({
    mutationFn: (patch: Partial<{ name: string; sortOrder: number; active: boolean }>) =>
      updateVariationType(type.id, patch),
    meta: { successMessage: 'Variation type saved.' },
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });

  return (
    <Card className={type.active ? '' : 'opacity-60'}>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          {type.name}
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
            {type.code}
          </span>
          {!type.active && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              inactive
            </span>
          )}
        </CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => update.mutate({ active: !type.active })}
          >
            {type.active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="outline" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing && (
          <TypeForm
            type={type}
            submitting={update.isPending}
            error={update.error}
            onSubmit={(input) => update.mutate({ name: input.name, sortOrder: input.sortOrder })}
            onCancel={() => setEditing(false)}
          />
        )}
        <OptionsEditor type={type} onChanged={onChanged} />
      </CardContent>
    </Card>
  );
}

function OptionsEditor({ type, onChanged }: { type: VariationType; onChanged: () => void }) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['variation-types'] });
    onChanged();
  };
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [deleting, setDeleting] = useState<VariationOption | null>(null);

  const add = useMutation({
    mutationFn: () => addVariationOption(type.id, { code: code.trim(), label: label.trim() }),
    meta: { successMessage: 'Value added.' },
    onSuccess: () => {
      setCode('');
      setLabel('');
      invalidate();
    },
  });
  const toggle = useMutation({
    mutationFn: (o: VariationOption) => updateVariationOption(o.id, { active: !o.active }),
    meta: { successMessage: 'Value updated.' },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteVariationOption(id),
    meta: { successMessage: 'Value deleted.' },
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {type.options.length === 0 && (
          <span className="text-xs text-muted-foreground">No options yet.</span>
        )}
        {type.options.map((o) => (
          <span
            key={o.id}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
              o.active ? '' : 'opacity-50'
            }`}
          >
            {o.label}
            <span className="font-mono text-[10px] text-muted-foreground">{o.code}</span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              title={o.active ? 'Deactivate' : 'Activate'}
              onClick={() => toggle.mutate(o)}
            >
              {o.active ? '⦸' : '✓'}
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              title="Delete"
              onClick={() => setDeleting(o)}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Value label</label>
          <Input className="h-8 w-32" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Small" />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] text-muted-foreground">Code</label>
          <Input
            className="h-8 w-24"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="S"
          />
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!code.trim() || !label.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          + Add value
        </Button>
      </div>
      {errText(add.error) && <p className="text-xs text-destructive">{errText(add.error)}</p>}
      {errText(remove.error) && <p className="text-xs text-destructive">{errText(remove.error)}</p>}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete value "${deleting?.label}"?`}
        description="Only allowed if no product offers this value. Otherwise deactivate it."
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.id);
        }}
      />
    </div>
  );
}

function TypeForm({
  type,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  type?: VariationType;
  submitting: boolean;
  error: unknown;
  onSubmit: (input: { code: string; name: string; sortOrder: number }) => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState(type?.code ?? '');
  const [name, setName] = useState(type?.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(type?.sortOrder ?? 0));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ code: code.toUpperCase(), name: name.trim(), sortOrder: Number(sortOrder) || 0 });
      }}
      className="grid grid-cols-1 gap-3 rounded-md border bg-muted/30 p-3 md:grid-cols-3"
    >
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Code</label>
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          disabled={!!type}
          placeholder="SIZE, COLOR"
          required
        />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Name</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Size" required />
      </div>
      <div className="space-y-1">
        <label className="text-xs text-muted-foreground">Sort order</label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
      </div>
      {errText(error) && <p className="text-sm text-destructive md:col-span-3">{errText(error)}</p>}
      <div className="flex gap-2 md:col-span-3">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : type ? 'Save' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
